import { Component, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { CapacitorBarcodeScanner, CapacitorBarcodeScannerTypeHint } from '@capacitor/barcode-scanner';
import { APP_TITLE } from 'src/constants/defaults';
import { environment } from 'src/environments/environment';
import { StorageProvider } from 'src/providers/storage.provider';
import { TranslatorProvider } from 'src/providers/translator.provider';
import { OauthCordova, RotaProvider } from 'src/shared/libraries';
import { LocalStorageService } from 'src/providers/local-storage.service';
import { QR_LOGIN } from 'src/constants/storage-keys';
import { SplashScreenProvider } from 'src/providers/splash-screen.provider';
import { AlertController, Platform } from '@ionic/angular';
import { AuthenticatedHttpService } from 'src/providers/authenticatedHttpService.service';
import * as CryptoJS from 'crypto-js';

@Component({
  selector: 'rota-page-login',
  styleUrl: 'login.page.scss',
  templateUrl: 'login.page.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class LoginPage {
  protected title: string;

  private alert: HTMLIonAlertElement;

  public oauth: OauthCordova;
  private identityProvider: RotaProvider;

  public isDesktopPlatform = false;

  public emailModel = "";
  public passwordModel = "";

  constructor(
    public http: AuthenticatedHttpService,
    public translator: TranslatorProvider,
    public router: Router,
    private storage: LocalStorageService,
    private storageProvider: StorageProvider,
    private splash: SplashScreenProvider,
    private alertController: AlertController,
    private platform: Platform,
  ) { }

  async ionViewWillEnter() {
    this.title = APP_TITLE;
    this.isDesktopPlatform = this.storageProvider.isDesktop();

    this.splash.hide();
  }

  async doMobileLogin() {
    this.oauth = new OauthCordova();
    this.identityProvider = new RotaProvider({
      clientId: environment.web.clientId,
      appScope: ["openid"],
      redirectUri: environment.web.redirect,
      authUrl: environment.web.identity + "/connect/authorize"
    });

    this.oauth.logInVia(this.identityProvider,
      {
        loadingText: this.translator.dictionary.Waiting_for_internet_connection,
        isAndroid: this.platform.is('android'),
      })
      .then(
        async (success: any) => {
          var authorizationCode = success["code"];

          this.splash.showWithLoading();

          this.http.initializeAuthViaCode(authorizationCode, async () => {
            await this.startApplication();
          }, () => this.splash.hide());

        },
        (error) => {
          console.log(error)
        })
      .catch(e => console.log(e));
  }

  async doDesktopLogin() {
    this.splash.showWithLoading();

    this.http.initializeAuth(this.emailModel, this.passwordModel,
      async () => {
        this.passwordModel = "";
        this.emailModel = "";
        await this.startApplication();
      },
      async () => {
        this.splash.hide();

        const alert = await this.alertController.create({
          header: this.translator.dictionary.Login_Login + ' / ' + this.translator.dictionary.Login_Password_Hint,
          message: this.translator.dictionary.Login_Invalid,
          cssClass: 'custom-alert',
          buttons: [this.translator.dictionary.General_Okay]
        })

        await alert.present();
      });
  }

  async doQRLogin() {
    try {
      const scanResponse = await CapacitorBarcodeScanner.scanBarcode({
        hint: CapacitorBarcodeScannerTypeHint.ALL,
        web: {
          showCameraSelection: true,
        }
      });

      const userData = scanResponse.ScanResult.split('\n');
      const userName = userData[0];
      const password = userData[1];

      const key = CryptoJS.enc.Latin1.parse(environment.crypt.key);
      const iv = CryptoJS.enc.Latin1.parse(environment.crypt.iv);

      const decryptedUserName = CryptoJS.AES.decrypt(userName, key, { iv: iv, mode: CryptoJS.mode.CBC });
      const decryptedPassword = CryptoJS.AES.decrypt(password, key, { iv: iv, mode: CryptoJS.mode.CBC });

      const login = decryptedUserName.toString(CryptoJS.enc.Utf8);
      const pwd = decryptedPassword.toString(CryptoJS.enc.Utf8);

      if (login != undefined && login.length > 0 && pwd != undefined && pwd.length > 0) {
        this.splash.showWithLoading();

        this.http.initializeAuth(login, pwd, async () => {
          await this.storage.set(QR_LOGIN, true);
          await this.startApplication();
        }, () => {
          this.showCustomAlert(this.translator.dictionary.General_Unauthorized_Title, this.translator.dictionary.General_Unauthorized_Text);
          this.splash.hide();
        });
      }
      else {
        this.showCustomAlert(this.translator.dictionary.General_Unauthorized_Title, this.translator.dictionary.General_Unauthorized_Text);
      }
    }
    catch (e) {
      console.log(e);
    }
  }

  async startApplication() {
    await this.router.navigate(['/tabs']);
  }

  desktopKeyboardHandler(event: any) {
    if (event.keyCode === 13) { // Enter
      this.doDesktopLogin();
    }
  }

  async showCustomAlert(titleText: string, message: string, delayInSeconds?: number) {
    this.alert = await this.alertController.create({
      header: titleText,
      message: message,
      cssClass: 'custom-alert',
      buttons: [
        {
          text: this.translator.dictionary.General_Okay,
          role: 'cancel'
        }]
    });

    await this.alert.present();

    if (delayInSeconds) {
      setTimeout(async () => {
        if (this.alert) await this.alert.dismiss();
      }, delayInSeconds * 1000);
    }
  }
}
